import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createMeeting,
  endMeeting,
  joinMeeting,
  startRecording,
} from "../apis/videoService";
import { useChimeVideo } from "../hooks/useChimeVideo";
import type { MeetingEndInfo } from "../services/chimeVideo";
import VideoBackgroundControls from "../components/VideoBackgroundControls";
import VideoExitConfirmation from "../components/VideoExitConfirmation";
import "./videoConsultant.css";

const RECORDING_START_ATTEMPTS = 3;
const RECORDING_RETRY_DELAY_MS = 750;

const VideoConsultant = () => {
  const { hostUuid } = useParams<{ hostUuid: string }>();
  const navigate = useNavigate();
  const startAttemptRef = useRef(false);
  const operationGenerationRef = useRef(0);
  const confirmedEndGenerationRef = useRef<number | null>(null);
  const endAttemptRef = useRef<{
    generation: number;
    result: ReturnType<typeof endMeeting>;
  } | null>(null);
  const [meetingUuid, setMeetingUuid] = useState<string | null>(null);
  const [shareableLink, setShareableLink] = useState("");
  const [isStarting, setIsStarting] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [confirmEndOpen, setConfirmEndOpen] = useState(false);
  const endingRef = useRef(false);
  const [invitationAccepted, setInvitationAccepted] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);

  const {
    connectionState,
    isMeetingActive,
    isConnecting,
    remoteParticipantState,
    error,
    warning,
    isMuted,
    isVideoEnabled,
    isLocalVideoReady,
    cameraState,
    requiresAudioActivation,
    requiresVideoActivation,
    isRecording,
    initialize,
    startMeeting,
    stopMeeting,
    toggleMute,
    retryLocalVideo,
    enableRawVideo,
    closeVideo,
    switchCamera,
    resumeAudioPlayback,
    resumeVideoPlayback,
    broadcastRecordingState,
    setLocalVideoElement,
    setRemoteVideoElement,
    setAudioElement,
    setOnMeetingEnded,
  } = useChimeVideo();

  const handleMeetingEnded = useCallback(
    (endInfo: MeetingEndInfo) => {
      operationGenerationRef.current += 1;
      endingRef.current = false;
      setConfirmEndOpen(false);
      setIsStarting(false);
      setIsEnding(false);
      setPageError(endInfo.message);
      void stopMeeting();
    },
    [stopMeeting],
  );

  useEffect(() => {
    setOnMeetingEnded(handleMeetingEnded);
    return () => setOnMeetingEnded(null);
  }, [handleMeetingEnded, setOnMeetingEnded]);

  useEffect(() => {
    return () => {
      operationGenerationRef.current += 1;
    };
  }, []);

  const handleCreateMeeting = useCallback(async () => {
    if (!hostUuid || startAttemptRef.current) {
      return;
    }

    startAttemptRef.current = true;
    endingRef.current = false;
    const generation = ++operationGenerationRef.current;
    let createdMeetingUuid: string | null = null;
    const cancelIfStale = async (): Promise<boolean> => {
      if (operationGenerationRef.current === generation) return false;
      // A confirmed end already owns cleanup; only abandoned starts need compensation.
      if (confirmedEndGenerationRef.current === generation) return true;
      const ending = endAttemptRef.current;
      if (ending?.generation === generation) {
        // The SDK/unmount already stopped local media. Observe the existing end
        // without holding a future meeting hostage to its network response.
        void ending.result.catch(async () => {
          if (createdMeetingUuid) {
            await endMeeting({
              meetingUuid: createdMeetingUuid,
              hostUuid,
            }).catch(() => undefined);
          }
        });
        return true;
      }
      if (createdMeetingUuid) {
        await endMeeting({ meetingUuid: createdMeetingUuid, hostUuid }).catch(
          () => undefined,
        );
      }
      await stopMeeting();
      return true;
    };
    setIsStarting(true);
    setPageError(null);
    try {
      const created = await createMeeting({ hostUuid });
      createdMeetingUuid = created.meetingUuid;
      if (await cancelIfStale()) return;
      setMeetingUuid(created.meetingUuid);
      setInvitationAccepted(created.invitationAccepted);
      setShareableLink(
        `${window.location.origin}/videoJoin/${created.meetingUuid}`,
      );

      const joined = await joinMeeting({
        meetingUuid: created.meetingUuid,
        hostUuid,
      });
      if (await cancelIfStale()) return;
      await initialize(joined.meeting, joined.attendee, {
        participantRole: joined.participantRole,
        expiresAt: joined.expiresAt,
        recordingStarted: joined.recordingStarted,
      });
      if (await cancelIfStale()) return;
      await startMeeting();
      if (await cancelIfStale()) return;

      let recordingStarted = false;
      for (
        let attempt = 0;
        attempt < RECORDING_START_ATTEMPTS && !recordingStarted;
        attempt += 1
      ) {
        if (await cancelIfStale()) return;
        try {
          await startRecording({ meetingUuid: created.meetingUuid, hostUuid });
          if (await cancelIfStale()) return;
          recordingStarted = true;
        } catch {
          if (await cancelIfStale()) return;
          if (attempt < RECORDING_START_ATTEMPTS - 1) {
            await new Promise((resolve) =>
              window.setTimeout(resolve, RECORDING_RETRY_DELAY_MS),
            );
            if (await cancelIfStale()) return;
          }
        }
      }
      if (recordingStarted) {
        broadcastRecordingState(true);
      } else {
        await endMeeting({ meetingUuid: created.meetingUuid, hostUuid }).catch(
          () => undefined,
        );
        await stopMeeting();
        setPageError("無法確認錄影狀態，為保護雙方權益，視訊已停止。請重試。");
      }
    } catch {
      if (createdMeetingUuid) {
        await endMeeting({ meetingUuid: createdMeetingUuid, hostUuid }).catch(
          () => undefined,
        );
      }
      await stopMeeting();
      if (operationGenerationRef.current === generation) {
        setPageError("建立視訊會議失敗，請重試。");
      }
    } finally {
      startAttemptRef.current = false;
      if (operationGenerationRef.current === generation) {
        setIsStarting(false);
      }
    }
  }, [
    broadcastRecordingState,
    hostUuid,
    initialize,
    startMeeting,
    stopMeeting,
  ]);

  const handleRetryInvitation = async () => {
    if (!hostUuid) return;
    setPageError(null);
    try {
      const response = await createMeeting({ hostUuid });
      setInvitationAccepted(response.invitationAccepted);
      if (!response.invitationAccepted) {
        setPageError("LINE 尚未接受邀請，請稍後再重送。");
      }
    } catch {
      setPageError("重送 LINE 邀請失敗，視訊會議仍可繼續。");
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareableLink);
    } catch {
      setPageError("複製失敗，請手動複製連結。");
    }
  };

  const handleEndCall = async () => {
    if (!meetingUuid || !hostUuid || endingRef.current) return;
    endingRef.current = true;
    setConfirmEndOpen(false);
    const generation = operationGenerationRef.current;
    setIsEnding(true);
    setPageError(null);
    try {
      const result = endMeeting({ meetingUuid, hostUuid });
      endAttemptRef.current = { generation, result };
      await result;
      confirmedEndGenerationRef.current = generation;
      if (operationGenerationRef.current !== generation) return;
      operationGenerationRef.current += 1;
      await stopMeeting();
      navigate(-1);
    } catch {
      if (operationGenerationRef.current !== generation) return;
      setPageError("未能結束伺服器上的會議，請重試。");
      setIsEnding(false);
      endingRef.current = false;
    }
  };

  const remoteStatusText = {
    waiting: "等待用戶加入...",
    "present-no-video": "用戶已加入，尚未開啟攝影機",
    "video-paused": "網路頻寬不足，對方畫面暫停，聲音仍可使用",
    left: "用戶已離開，可於時限內重新加入",
    "video-on": "",
  }[remoteParticipantState];

  return (
    <div className="video-page">
      <VideoExitConfirmation open={confirmEndOpen} busy={isEnding} endsMeeting
        onCancel={() => setConfirmEndOpen(false)} onConfirm={handleEndCall} />
      {!isMeetingActive &&
        !isConnecting &&
        !isStarting &&
        !error &&
        !pageError && (
          <div className="start-container">
            <h2>視訊諮詢</h2>
            <button onClick={handleCreateMeeting} className="start-btn">
              開始視訊會議
            </button>
          </div>
        )}

      {!isMeetingActive && (isConnecting || isStarting) && (
        <div className="connecting-container">
          <div className="spinner" />
          <p>正在建立視訊連線...</p>
        </div>
      )}

      {isMeetingActive && (
        <>
          <audio ref={setAudioElement} autoPlay className="meeting-audio" />
          <div
            className={`meeting-status ${connectionState === "reconnecting" ? "reconnecting" : ""}`}
            aria-live="polite"
          >
            {connectionState === "reconnecting"
              ? "網路中斷，正在重新連線。保留畫面為非即時影像。"
              : remoteStatusText}
          </div>

          {isRecording && (
            <div className="recording-indicator">
              <span className="recording-dot" />
              <span>錄影中</span>
            </div>
          )}

          {!invitationAccepted && (
            <div className="video-warning" role="alert">
              LINE 尚未接受視訊邀請。
              <button type="button" onClick={handleRetryInvitation}>
                重送邀請
              </button>
            </div>
          )}
          {requiresAudioActivation && (
            <div className="audio-activation" role="alert">
              <span>手機瀏覽器需要您手動開啟聲音。</span>
              <button type="button" onClick={resumeAudioPlayback}>
                點一下開啟聲音
              </button>
            </div>
          )}
          {requiresVideoActivation && (
            <div className="audio-activation" role="alert">
              <span>手機瀏覽器已暫停畫面播放。</span>
              <button type="button" onClick={resumeVideoPlayback}>
                點一下恢復畫面
              </button>
            </div>
          )}
          {warning && <div className="video-warning">{warning}</div>}
          {pageError && <div className="video-warning">{pageError}</div>}

          {shareableLink && (
            <div className="share-link-container">
              <p>LINE 未送達時可提供此備用連結：</p>
              <div className="link-box">
                <input type="text" value={shareableLink} readOnly />
                <button onClick={handleCopyLink}>複製</button>
              </div>
            </div>
          )}

          <div className="video-container">
            <div className="remote-video-container">
              <video
                ref={setRemoteVideoElement}
                className="remote-video"
                autoPlay
                playsInline
              />
              {remoteParticipantState !== "video-on" && (
                <div className="remote-placeholder">
                  <span className="avatar">👤</span>
                  <span>{remoteStatusText}</span>
                </div>
              )}
            </div>

            <div
              className={`local-video-container ${
                !isLocalVideoReady ? "loading" : ""
              }`}
            >
              <video
                ref={setLocalVideoElement}
                className="local-video"
                data-background-mode={cameraState.mode}
                autoPlay
                playsInline
                muted
              />
              {!isLocalVideoReady && (
                <span className="local-video-status">
                  {cameraState.status === "loading" ? "攝影機準備中" : isVideoEnabled ? "攝影機尚未就緒" : "攝影機未開啟"}
                </span>
              )}
            </div>
          </div>

          <div className="video-controls" role="group" aria-label="通話控制">
            <button
              className={`control-btn ${isMuted ? "muted" : ""}`}
              onClick={toggleMute}
              title={isMuted ? "取消靜音" : "靜音"}
            >
              <i
                className={
                  isMuted ? "fas fa-microphone-slash" : "fas fa-microphone"
                }
              />
            </button>
            <VideoBackgroundControls cameraState={cameraState} isLocalVideoReady={isLocalVideoReady}
              connected={connectionState === "connected"} isReconnecting={connectionState === "reconnecting"}
              retryProtectedVideo={retryLocalVideo}
              enableRawVideo={enableRawVideo} closeVideo={closeVideo} switchCamera={switchCamera} />
            <button
              className="control-btn leave-call"
              onClick={() => setConfirmEndOpen(true)}
              disabled={isEnding}
              aria-busy={isEnding}
              aria-label={isEnding ? "正在結束通話" : "結束通話"}
              title="結束通話"
            >
              <i className={isEnding ? "fas fa-spinner fa-spin" : "fas fa-phone-slash"} />
            </button>
          </div>
        </>
      )}

      {(error || (!isMeetingActive && pageError)) &&
        !isConnecting &&
        !isStarting && (
          <div className="error-container">
            <p>{error || pageError}</p>
            <button onClick={handleCreateMeeting}>重試</button>
          </div>
        )}
    </div>
  );
};

export default VideoConsultant;
